import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Search, 
  ArrowUpDown, 
  Store, 
  ChevronRight,
  Award
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VendorsPage = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('spend-desc');

  const fetchVendors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vendors-analytics/company`, { withCredentials: true });
      setVendors(response.data);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error('Failed to load vendors');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const filteredVendors = useMemo(() => {
    let result = [...vendors];
    if (searchTerm) {
      result = result.filter(v =>
        v.vendor_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    switch (sortBy) {
      case 'spend-desc': result.sort((a, b) => b.total_spent - a.total_spent); break;
      case 'spend-asc': result.sort((a, b) => a.total_spent - b.total_spent); break;
      case 'name-asc': result.sort((a, b) => a.vendor_name.localeCompare(b.vendor_name)); break;
      case 'name-desc': result.sort((a, b) => b.vendor_name.localeCompare(a.vendor_name)); break;
      case 'orders-desc': result.sort((a, b) => b.order_count - a.order_count); break;
      default: break;
    }
    return result;
  }, [vendors, searchTerm, sortBy]);

  const totalSpend = useMemo(() => vendors.reduce((s, v) => s + v.total_spent, 0), [vendors]);

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return 'N/A'; }
  };
  const getDeliveryLabel = (avgDays) => {
    if (avgDays === null || avgDays === undefined) return { text: 'No data', className: 'text-zinc-400' };
    if (avgDays <= 0) return { text: `${Math.abs(avgDays)}d early`, className: 'text-green-600 font-medium' };
    if (avgDays <= 2) return { text: `${avgDays}d late`, className: 'text-amber-500 font-medium' };
    return { text: `${avgDays}d late`, className: 'text-red-500 font-medium' };
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="vendors-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wide text-zinc-900 mb-1">Vendors</h1>
          <p className="text-zinc-500 text-sm">
            <span className="font-mono">{vendors.length}</span> vendor{vendors.length !== 1 ? 's' : ''} &middot; <span className="font-mono">{formatCurrency(totalSpend)}</span> total spend
          </p>
        </div>
        {vendors.length > 0 && (
          <button
            onClick={() => navigate('/vendors/scorecard')}
            data-testid="generate-scorecard-btn"
            className="inline-flex items-center gap-2 h-10 px-5 bg-primary text-black font-heading font-bold uppercase tracking-wider text-xs hover:bg-primary-hover transition-colors"
          >
            <Award className="w-4 h-4" />
            Generate Scorecard
          </button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text"
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="vendor-search-input"
            className="w-full h-10 border border-zinc-200 bg-white text-zinc-900 text-sm pl-10 pr-4 focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <div className="relative">
          <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            data-testid="vendor-sort"
            className="h-10 border border-zinc-200 bg-white text-zinc-900 text-sm pl-10 pr-8 focus:outline-none focus:border-primary appearance-none"
          >
            <option value="spend-desc">Highest Spend</option>
            <option value="spend-asc">Lowest Spend</option>
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
            <option value="orders-desc">Most Orders</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-3" data-testid="vendors-skeleton">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white border border-zinc-200 p-4 sm:p-5 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-zinc-100" />
                <div className="h-5 w-40 bg-zinc-100" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[1,2,3,4].map(j => (
                  <div key={j}><div className="h-3 w-16 bg-zinc-100 mb-1" /><div className="h-4 w-20 bg-zinc-100" /></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : filteredVendors.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-8 sm:p-12 text-center" data-testid="vendors-empty-state">
          <Store className="w-16 h-16 mx-auto mb-4 text-zinc-300" />
          <h2 className="font-heading text-xl font-bold uppercase tracking-wide text-zinc-900 mb-2">
            {vendors.length === 0 ? 'No Vendors Yet' : 'No Matching Vendors'}
          </h2>
          <p className="text-zinc-500 text-sm max-w-md mx-auto">
            {vendors.length === 0
              ? 'Vendors appear here as you add orders. Create a material order to get started.'
              : 'Try adjusting your search.'
            }
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredVendors.map((vendor) => {
            const delivery = getDeliveryLabel(vendor.avg_delivery_days);
            const spendPercent = totalSpend > 0 ? (vendor.total_spent / totalSpend) * 100 : 0;
            return (
              <button
                key={vendor.vendor_name}
                onClick={() => navigate(`/vendors/${encodeURIComponent(vendor.vendor_name)}`)}
                data-testid={`vendor-card-${vendor.vendor_name.replace(/\s+/g, '-').toLowerCase()}`}
                className="w-full bg-white border border-zinc-200 p-4 sm:p-5 text-left hover:border-primary transition-colors group"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 bg-zinc-100 group-hover:bg-primary/10 flex items-center justify-center flex-shrink-0 transition-colors">
                        <Store className="w-4 h-4 text-zinc-500" />
                      </div>
                      <h3 className="font-heading text-base font-bold uppercase tracking-wide text-zinc-900 truncate">{vendor.vendor_name}</h3>
                    </div>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <p className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400">Total Spend</p>
                        <p className="text-base font-mono font-semibold text-zinc-900">{formatCurrency(vendor.total_spent)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400">Orders</p>
                        <p className="text-base font-mono font-semibold text-zinc-900">{vendor.order_count}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400">Last Order</p>
                        <p className="text-sm font-mono text-zinc-600">{formatDate(vendor.last_order_date)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-400">Delivery</p>
                        <p className={`text-sm font-mono ${delivery.className}`}>{delivery.text}</p>
                      </div>
                    </div>

                    <div className="mt-3">
                      <div className="h-1.5 bg-zinc-100 overflow-hidden">
                        <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(spendPercent, 100)}%` }} />
                      </div>
                      <p className="text-[10px] font-mono text-zinc-400 mt-1">
                        {spendPercent.toFixed(1)}% OF TOTAL SPEND
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-300 group-hover:text-primary flex-shrink-0 mt-3 transition-colors" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VendorsPage;
